// Direction-based relationship panels (SEP-227) — the pure half of the split.
//
// The Contact, Suitability Record and Client detail pages showed ONE table of
// every relationship the record carries, in both directions at once. A reader
// who wanted the people had to read past the mandates, and a reader who wanted
// the mandates had to read past the people. The pages now stack one panel for
// each COUNTERPART TIER, and this module holds the rules: which panels an endpoint
// type gets, which rows land in each one, and the A-Z sort keys the columns use.
//
// THE PARTITION IS BY COUNTERPART TIER, AND NEVER BY `via`. A `via` row is one
// the server INFERRED one hop through a suitability record (SEP-188); a row
// without one is recorded. That distinction is real, but it is not a direction:
// a contact can hold a RECORDED `contact_account` link (a SIPP provider against
// an Account) beside an inferred one, and the server suppresses the
// inferred row whenever the recorded row exists. A partition by `via` would
// therefore strand the recorded rows of a tier in a panel titled for inferred
// ones, and the two kinds would change places as soon as somebody recorded the
// link. The `via` marker in the Party 2 cell already tells the two apart inside
// one panel, which is the job it was built for.
//
// This module holds no React, so the panel rules are testable without a DOM.

import { isSingleRoleRelationshipKind } from '@septima/contract';
import type { RelationshipEndpointType, RelationshipRow } from '@septima/contract';

export interface RelationshipPanelSpec {
    /** Unique within one endpoint type: the React key, and the suffix of the add
     *  button's `data-testid` that a scenario pins its step to. */
    key: string;
    title: string;
    counterpartTier: RelationshipEndpointType;
    /** Whether this panel offers an add affordance at all. It is a separate
     *  question from `editable`, which is about the READER's persona. */
    addable: boolean;
    emptyTitle: string;
}

const CONTACT_PANELS: readonly RelationshipPanelSpec[] = [
    {
        key: 'sur',
        title: 'Relationships – SUR',
        counterpartTier: 'suitability-record',
        addable: true,
        emptyTitle: 'No suitability record relationships recorded',
    },
    {
        // A contact CAN hold a recorded link to an Account — a SIPP
        // provider or an accountant (PRD §9.6) — so the panel stays addable.
        // The title says "Indirect" because most of its rows are inferred
        // through the record; the row-level `via` marker says which are.
        //
        // THE KEY STAYS `client` THOUGH THE TITLE NO LONGER DOES. The key is
        // the suffix of the add button's `data-testid`, which a scenario pins,
        // and a testid is a handle and not a label.
        key: 'client',
        title: 'Relationships – Account group (Indirect)',
        counterpartTier: 'account',
        addable: true,
        emptyTitle: 'No account group relationships recorded',
    },
    {
        // The registered holder itself — the CRM anchor tier. Its rows are the
        // primary-contact links, which the contacts ingest mints and this
        // panel is the contact-side home of.
        key: 'registered-client',
        title: 'Relationships – Client',
        counterpartTier: 'client',
        addable: true,
        emptyTitle: 'No client relationships recorded',
    },
    {
        key: 'personal',
        title: 'Relationships – Contact (Personal)',
        counterpartTier: 'contact',
        addable: true,
        emptyTitle: 'No personal relationships recorded',
    },
];

const CLIENT_PANELS: readonly RelationshipPanelSpec[] = [
    {
        key: 'sur',
        title: 'Relationships – SUR',
        counterpartTier: 'suitability-record',
        addable: true,
        emptyTitle: 'No suitability record relationships recorded',
    },
    {
        key: 'contact',
        title: 'Relationships – Contact (Indirect)',
        counterpartTier: 'contact',
        addable: true,
        emptyTitle: 'No contact relationships recorded',
    },
];

// The registered-holder tier (`client`). It reaches the Contact tier and
// nothing else — `relationshipPairKind` names `contact_client` and no other
// pair — so it stacks exactly one panel, the mirror of the Contact page's
// `registered-client` one.
const REGISTERED_CLIENT_PANELS: readonly RelationshipPanelSpec[] = [
    {
        key: 'contact',
        title: 'Relationships – Contact',
        counterpartTier: 'contact',
        addable: true,
        emptyTitle: 'No contact relationships recorded',
    },
];

const RECORD_PANELS: readonly RelationshipPanelSpec[] = [
    {
        key: 'contact',
        title: 'Relationships – Contact',
        counterpartTier: 'contact',
        addable: true,
        emptyTitle: 'No contact relationships recorded',
    },
    {
        // As on the Contact page, the key stays `client` because a scenario
        // pins the add button's testid, while the title names the tier.
        key: 'client',
        title: 'Relationships – Account group',
        counterpartTier: 'account',
        addable: true,
        emptyTitle: 'No account group relationships recorded',
    },
];

/**
 * The panels one detail page stacks, in the order it stacks them.
 *
 * Each endpoint type gets ONE panel for each tier it can link to, so every row
 * the server returns has exactly one home. A contact reaches all four tiers,
 * because it also links to another contact and to the registered holder; the
 * Account tier and the record tier reach two each; the registered holder
 * reaches the Contact tier alone.
 */
export function panelsForEndpoint(
    endpointType: RelationshipEndpointType,
): readonly RelationshipPanelSpec[] {
    if (endpointType === 'contact') return CONTACT_PANELS;
    if (endpointType === 'account') return CLIENT_PANELS;
    // The Client tier now carries its own panel, so a primary-contact anchor
    // row renders on the holder's side as well as on the contact's. The branch
    // stays explicit because the record panels are the WRONG answer for a
    // Client endpoint, and a fall-through would give them.
    if (endpointType === 'client') return REGISTERED_CLIENT_PANELS;
    return RECORD_PANELS;
}

/** Whether a row belongs to a panel. The counterpart tier decides, and nothing
 *  else does — see the header note about `via`. */
export function rowBelongsToPanel(row: RelationshipRow, spec: RelationshipPanelSpec): boolean {
    return row.counterpartType === spec.counterpartTier;
}

/**
 * The role visible at each end.
 *
 * A SINGLE-ROLE KIND (`isSingleRoleRelationshipKind`) carries the role on
 * the CONTACT side only; the other end mirrors the key by storage convention and
 * must never be shown — a suitability record must not describe itself as
 * "Nominated Representative" (`vendor/septima-ontology-wealth/packages/ontology-wealth/specs/client/relationship.md`). `showOwn`
 * is the current record's end, which the table shows as Role 1; `showCounterpart`
 * is Party 2's end, which the table shows as Role 2.
 */
export function roleVisibility(row: RelationshipRow): {
    showOwn: boolean;
    showCounterpart: boolean;
} {
    const singleRoleKind = isSingleRoleRelationshipKind(row.kind);
    return {
        showCounterpart: !singleRoleKind || row.counterpartType === 'contact',
        showOwn: !singleRoleKind || row.counterpartType !== 'contact',
    };
}

/**
 * Sort key for Role 1. A hidden end sorts as null, exactly as it renders as a
 * dash: the mirrored key it stores is not a value a reader can see, so it must
 * not be a value the reader can order the table by.
 */
export function role1SortValue(row: RelationshipRow): string | null {
    return roleVisibility(row).showOwn ? row.ownRole.label : null;
}

/** Sort key for Role 2 — the mirror of {@link role1SortValue}. */
export function role2SortValue(row: RelationshipRow): string | null {
    return roleVisibility(row).showCounterpart ? row.counterpartRole.label : null;
}

/** Sort key for Party 2. Null for a counterpart outside the caller's read: the
 *  cell shows "Not in your book", which names nobody to sort by. */
export function endpoint2SortValue(row: RelationshipRow): string | null {
    return row.counterpartName;
}
